import { useState, useMemo } from 'react';
import WithLoadingErrorWrapper from '@/components/with-loading-error-wrapper';
import { CourseCard } from '@/components';
import { useGetCoursesQuery, useGetBoardsQuery } from '@/state/course';
import HelmetTitle from '@/components/helmet-title';
import type { ApiErrorResponse } from '@/types';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Search, Plus } from 'lucide-react';
import CreateCourseForm from '@/components/create-course-modal/create-course-form';
import { useTenant } from '@/hooks';
import { skipToken } from '@reduxjs/toolkit/query';
import { useNavigate } from 'react-router-dom';
import { useClientPermissions } from '@/hooks/use-client-permissions';

const Courses = () => {
    const navigate = useNavigate();
    const { tenant } = useTenant();
    const [selectedBoardId, setSelectedBoardId] = useState<string>('all');
    const [searchQuery, setSearchQuery] = useState('');
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const { hasAIModule, isTeacher, isSchoolAdmin } = useClientPermissions();
    const canGenerateCourse = hasAIModule && (isTeacher || isSchoolAdmin);
    const canCreateCourse = isTeacher || isSchoolAdmin;

    const {
        data: courses,
        isLoading: isCoursesLoading,
        error: coursesError,
        isFetching: isCoursesFetching,
        refetch: refetchCourses,
    } = useGetCoursesQuery(
        tenant
            ? {
                tenant,
                ...(selectedBoardId !== 'all' ? { board: parseInt(selectedBoardId) } : {}),
            }
            : skipToken
    );

    const {
        data: boards,
        isLoading: isBoardsLoading,
        error: boardsError,
    } = useGetBoardsQuery(tenant ? { tenant } : skipToken);

    const filteredBoards = useMemo(() => {
        return boards?.filter(board => board.courses > 0) || [];
    }, [boards]);

    const filteredCourses = useMemo(() => {
        if (!courses) return [];
        return courses.filter(course =>
            course.course_name.toLowerCase().includes(searchQuery.toLowerCase())
        );
    }, [courses, searchQuery]);

    const isLoading = isCoursesLoading || isBoardsLoading;
    const error = (coursesError || boardsError) as ApiErrorResponse;

    return (
        <>
            <HelmetTitle title="Courses" />
            <div className="container mx-auto px-4 py-8">
                {/* Sticky Header */}
                <div className="sticky top-0 z-10 bg-background/95 backdrop-blur-sm pb-6 mb-8 border-b rounded-md px-4">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 py-4">
                        <h1 className="text-3xl font-bold">Courses</h1>

                        <div className="flex items-center gap-3">
                            {canCreateCourse ? (
                                <Button
                                    onClick={() => setIsCreateModalOpen(true)}
                                    className="shrink-0 px-2 sm:px-4"
                                    aria-label="Create course"
                                >
                                    <Plus className="h-4 w-4 sm:mr-2" />
                                    <span className="hidden sm:inline">Create Course</span>
                                </Button>
                            ) : null}

                            <div className="relative w-full md:w-72">
                                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                                <Input
                                    placeholder="Search courses..."
                                    className="pl-9 bg-background/50"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Boards Tabs */}
                    {filteredBoards.length > 0 && (
                        <Tabs
                            value={selectedBoardId}
                            onValueChange={setSelectedBoardId}
                            className="w-full"
                        >
                            <TabsList className="flex flex-wrap h-auto gap-2 bg-transparent p-0">
                                <TabsTrigger
                                    value="all"
                                >
                                    All Courses
                                </TabsTrigger>
                                {filteredBoards.map((board) => (
                                    <TabsTrigger
                                        key={board.id}
                                        value={board.id.toString()}
                                    >
                                        {board.board_name}
                                    </TabsTrigger>
                                ))}
                            </TabsList>
                        </Tabs>
                    )}
                </div>

                <WithLoadingErrorWrapper
                    loading={isLoading}
                    error={error}
                    isFetching={isCoursesFetching}
                    onRetry={refetchCourses}
                >
                    {filteredCourses.length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {filteredCourses.map((course) => (
                                <CourseCard
                                    key={course.id}
                                    course={course}
                                    onClick={() => {
                                        if (!tenant) return;
                                        navigate(`/${tenant}/courses/${course.id}`);
                                    }}
                                />
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-12">
                            <p className="text-muted-foreground">
                                {searchQuery ? 'No courses match your search.' : 'No courses available.'}
                            </p>
                        </div>
                    )}
                </WithLoadingErrorWrapper>
            </div>

            <CreateCourseForm
                isOpen={isCreateModalOpen}
                onClose={() => setIsCreateModalOpen(false)}
                canGenerateCourse={canGenerateCourse}
            />
        </>
    );
};

export default Courses;
